import { Button } from 'copilot-design-system';
import { useFormikContext } from 'formik';
import { useEffect } from 'react';

import { CLIENT_TYPE_INDIVIDUAL } from 'src/constants';
import {
  OnboardingContentLayout,
  OnboardingQuestionLabel,
} from 'src/routes/onboarding/setup/components/OnboardingContentLayout';
import { ToggleChipGroup } from 'src/routes/onboarding/setup/components/ToggleChipGroup';
import {
  CLIENT_COUNT_OPTIONS,
  CLIENT_TYPE_OPTIONS,
  COMPANY_REVENUE_OPTIONS,
} from 'src/routes/onboarding/setup/components/who-you-serve/constants';
import { useSetFormPageInfo } from 'src/routes/onboarding/setup/context/formPageInfoContext';
import {
  OnboardingFormValues,
  OnboardingStep,
  TrackedEventsType,
} from 'src/routes/onboarding/setup/types';
import { TrackEvent } from 'src/utils/AnalyticsUtils';

export const WhoYouServe = ({
  trackedEvents,
  onAnalyticsEventTracked,
}: {
  trackedEvents: TrackedEventsType;
  onAnalyticsEventTracked: (eventName: OnboardingStep) => void;
}) => {
  const { setFieldValue, values } = useFormikContext<OnboardingFormValues>();

  const setFormPageInfo = useSetFormPageInfo<OnboardingFormValues>();
  useEffect(() => {
    setFormPageInfo({
      submitHandler: ({ values: submitValues }) => {
        if (!trackedEvents['who-you-serve']) {
          TrackEvent('Onboarding - Who You Serve', {
            clientType: submitValues.clientType,
            clientCount: submitValues.clientCount,
            companyRevenue: submitValues.companyRevenue,
            default_channel_type:
              values.clientType === CLIENT_TYPE_INDIVIDUAL
                ? 'client'
                : 'company',
            enable_companies:
              values.clientType === CLIENT_TYPE_INDIVIDUAL ? 'FALSE' : 'TRUE',
          });

          // update the tracked events
          onAnalyticsEventTracked('who-you-serve');
        }
      },
    });
  }, [setFormPageInfo, TrackEvent, trackedEvents, onAnalyticsEventTracked]);

  return (
    <>
      <OnboardingContentLayout
        title="Who you serve"
        description="Tell us about the individuals or businesses you work with so we can
        tailor your workspace."
      >
        <div>
          <OnboardingQuestionLabel>
            What types of clients do you primarily serve?
          </OnboardingQuestionLabel>
          <ToggleChipGroup
            options={CLIENT_TYPE_OPTIONS}
            value={values.clientType}
            onOptionClick={(value, label) => {
              setFieldValue('clientType', value);
              setFieldValue('clientTypeLabel', label);
            }}
            numColumns={2}
          />
        </div>

        <div>
          <OnboardingQuestionLabel>
            How many clients did you serve last year?
          </OnboardingQuestionLabel>
          <ToggleChipGroup
            options={CLIENT_COUNT_OPTIONS}
            value={values.clientCount}
            onOptionClick={(value, label) => {
              setFieldValue('clientCount', value);
              setFieldValue('clientCountLabel', label);
            }}
          />
        </div>

        <div>
          <OnboardingQuestionLabel>
            What was your business revenue last year?
          </OnboardingQuestionLabel>
          <ToggleChipGroup
            options={COMPANY_REVENUE_OPTIONS}
            value={values.companyRevenue}
            onOptionClick={(value, label) => {
              setFieldValue('companyRevenue', value);
              setFieldValue('companyRevenueLabel', label);
            }}
          />
        </div>
      </OnboardingContentLayout>

      <div className="w-full sm:max-w-[360px] text-lg ml-auto mr-auto mt-10">
        <Button className="w-full h-12" label="Continue" type="submit" />
        <Button
          className="w-full h-12 mt-2"
          label="Skip"
          type="submit"
          variant="text"
        />
      </div>
    </>
  );
};
